import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Radial_Gradients = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">☀️ Advanced CSS: Radial Gradients</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        A <b>radial gradient</b> is a gradient that radiates outward from a
        center point. Unlike linear gradients that move along a straight line,
        radial gradients expand in all directions, forming <b>circles</b> or{" "}
        <b>ellipses</b>. They are useful for spotlight effects, glowing
        backgrounds, or modern UI elements.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Created with <code>radial-gradient()</code> or{" "}
            <code>repeating-radial-gradient()</code>.
          </li>
          <li>
            Supports <b>circle</b> and <b>ellipse</b> shapes.
          </li>
          <li>
            You can control the <b>size</b> (closest-side, farthest-corner,
            etc.).
          </li>
          <li>
            Gradients can be <b>centered</b> or positioned anywhere with{" "}
            <code>at</code>.
          </li>
          <li>
            Repeating radial gradients create stripe-like circular patterns.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Basic syntax */
background: radial-gradient(shape size at position, color1, color2, ...);

/* Examples */
background: radial-gradient(circle, red, yellow, green);
background: radial-gradient(ellipse at top left, #f87171, #60a5fa);
background: repeating-radial-gradient(circle, #22d3ee 10px, #818cf8 20px);`}
        />
      </div>

      {/* Circle Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">🎯 Circle Gradient</h2>
        <p>A simple circular gradient centered inside the element.</p>
        <div
          style={{
            background: "radial-gradient(circle, #fbbf24, #f97316, #dc2626)",
            height: "120px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: radial-gradient(circle, #fbbf24, #f97316, #dc2626);
}`}
        />
      </div>

      {/* Ellipse Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">🔵 Ellipse Gradient</h2>
        <p>
          By default, radial gradients are elliptical unless explicitly set to{" "}
          <code>circle</code>.
        </p>
        <div
          style={{
            background:
              "radial-gradient(ellipse at center, #34d399, #10b981, #065f46)",
            height: "120px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: radial-gradient(ellipse at center, #34d399, #10b981, #065f46);
}`}
        />
      </div>

      {/* Positioned Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">📍 Positioned Radial Gradient</h2>
        <p>
          You can place the gradient origin anywhere using{" "}
          <code>at position</code>.
        </p>
        <div
          style={{
            background:
              "radial-gradient(circle at top left, #60a5fa, #3b82f6, #1e40af)",
            height: "120px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: radial-gradient(circle at top left, #60a5fa, #3b82f6, #1e40af);
}`}
        />
      </div>

      {/* Repeating Radial Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">🔁 Repeating Radial Gradient</h2>
        <p>
          Repeating radial gradients let you create stripe-like or ripple
          effects.
        </p>
        <div
          style={{
            background:
              "repeating-radial-gradient(circle, #22d3ee 10px, #818cf8 20px)",
            height: "120px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: repeating-radial-gradient(circle, #22d3ee 10px, #818cf8 20px);
}`}
        />
      </div>

      {/* Overlay Example */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Overlay with Radial Gradient</h2>
        <p>
          You can use <code>rgba()</code> or <code>transparent</code> colors to
          create spotlight overlays.
        </p>
        <div
          style={{
            background:
              "radial-gradient(circle at center, rgba(0,0,0,0.5), transparent), url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80') center/cover no-repeat",
            height: "200px",
            borderRadius: "12px",
            marginBottom: "1rem",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            textShadow: "1px 1px 4px rgba(0,0,0,0.7)",
          }}
        >
          🎇 Spotlight Effect with Radial Gradient
        </div>
        <CodeCopyBlock
          code={`div {
  background: radial-gradient(circle at center, rgba(0,0,0,0.5), transparent),
              url("image.jpg") center/cover no-repeat;
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <code>repeating-radial-gradient</code> for artistic
            patterns.
          </li>
          <li>
            Combine radial gradients with <b>linear gradients</b> for layered
            effects.
          </li>
          <li>
            Use radial gradients as <b>masks</b> for spotlight transitions.
          </li>
          <li>
            Mix with <code>background-blend-mode</code> for advanced overlays.
          </li>
          <li>
            Explore <b>conic gradients</b> to create circular progress
            indicators.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Radial_Gradients;
